import { useState } from "react";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { asyncPool } from "../../../../utils/asyncPool";
import { useCreatePromptHistory, useTransformNursesCsv, useTransformShiftCsv } from "../../hooks";
import { earliestDateInCsv, SHIFTS_DATE_COLUMN } from "../../loadOrder";
import { type DataLoadFile, fileTypeOf } from "./consts";
import { errorMessage, nonEmptyTabInstructions } from "./dataLoadPayload";

/** The original file is uploaded to S3 per prompt-history row; a dozen at once stalls the run. */
const PROMPT_HISTORY_CONCURRENCY = 3;

/**
 * Transforms are parallel because they are read-only and LLM-bound — but not unbounded. Each one
 * re-uploads the file AND drives a multi-attempt pipeline per sheet, so it is strictly heavier than
 * the prompt-history upload above; firing fifteen at once saturates the same uplink that cap exists
 * to protect, and every progress bar then stalls together instead of finishing front-to-back.
 */
const TRANSFORM_CONCURRENCY = 3;

type Params = {
	departmentId: string | undefined;
	files: DataLoadFile[];
	updateFile: (fileId: string, partial: Partial<DataLoadFile>) => void;
	onDone: () => void;
};

/**
 * Phase A + B of a run: a prompt-history row for every file, then a transform for each file in
 * transform mode.
 *
 * Transforms run in parallel on purpose — they are read-only, LLM-bound, and the slow step. Only the
 * loads have to be serialized.
 */
export function useDataLoadTransform({ departmentId, files, updateFile, onDone }: Params) {
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const [isRecording, setIsRecording] = useState(false);
	const [isTransforming, setIsTransforming] = useState(false);

	const { mutateAsync: createPromptHistory } = useCreatePromptHistory();
	const { mutateAsync: transformNursesCsv } = useTransformNursesCsv();
	const { mutateAsync: transformShiftCsv } = useTransformShiftCsv();

	/**
	 * Archive the source file and its prompt. Direct-mode files get a row too, so an as-is upload is
	 * still traceable back to what was sent. Best-effort: the hook snackbars its own failure, and a
	 * missing row only costs the load its `prompt_history_id` stamp.
	 */
	const recordPromptHistory = async (entry: DataLoadFile): Promise<string | null> => {
		if (!departmentId || !entry.kind) return null;
		try {
			const created = await createPromptHistory({
				departmentId,
				fileType: fileTypeOf(entry.kind),
				prompt: entry.mode === "transform" ? entry.prompt : "",
				tabInstructions: nonEmptyTabInstructions(entry.tabInstructions),
				file: entry.file,
			});
			return created.id;
		} catch {
			return null;
		}
	};

	const transformOne = async (entry: DataLoadFile, promptHistoryId: string | null): Promise<boolean> => {
		if (!departmentId || !entry.kind) return false;
		const instructions = entry.prompt.trim() || undefined;
		try {
			if (entry.kind === "roster") {
				const csvBuffer = await transformNursesCsv({ departmentId, file: entry.file, instructions });
				updateFile(entry.id, { transformedData: csvBuffer, failedTabs: [], transformError: null, progress: null, promptHistoryId });
				return true;
			}
			const tabInstructions = nonEmptyTabInstructions(entry.tabInstructions);
			const { csvBuffer, failedTabs } = await transformShiftCsv({
				departmentId,
				file: entry.file,
				isTemplate: entry.kind === "template",
				instructions,
				tabInstructions: Object.keys(tabInstructions).length > 0 ? tabInstructions : undefined,
				sheetNames: entry.sheetNames.length > 1 ? entry.sheetNames : undefined,
				// The hook reports progress per call; a batch owns a separate figure per file.
				onProgress: (progress) => updateFile(entry.id, { progress }),
			});
			// Success means a CSV came back, NOT that every tab was clean: a file with failed tabs still
			// has rows the operator can preview, filter and load, and PreviewCard shows the warnings.
			updateFile(entry.id, {
				transformedData: csvBuffer,
				failedTabs,
				transformError: null,
				progress: null,
				promptHistoryId,
				earliestDate: entry.kind === "shifts" ? earliestDateInCsv(csvBuffer, SHIFTS_DATE_COLUMN) : null,
			});
			return true;
		} catch (error) {
			// The row exists (the file is archived) but the id is dropped so a retry creates a fresh one.
			updateFile(entry.id, { transformError: errorMessage(error), transformedData: null, progress: null, promptHistoryId: null });
			trackError("data_load_transform_file_error", { department_id: departmentId, kind: entry.kind, error_message: errorMessage(error) });
			return false;
		}
	};

	const reportOutcome = (failed: number, total: number) => {
		// A run where every transform failed still advances to the results step: the per-file error
		// rows there are the only place the failures are visible, and each one carries its own Remove
		// and retry. What changes is the reporting — this is an error, not a partial success.
		if (failed === total && !files.some((entry) => entry.mode === "direct")) {
			trackError("data_load_run_error", { department_id: departmentId, file_count: files.length, transformed_count: 0 });
			showSnackbar("No file transformed — see the errors below", "error");
			return;
		}
		track("data_load_run_success", { department_id: departmentId, transformed_count: total - failed, failed_count: failed });
		if (failed > 0) showSnackbar(`${failed} of ${total} file(s) failed to transform — see below`, "warning");
	};

	const recordAll = async (): Promise<Map<string, string | null>> => {
		setIsRecording(true);
		try {
			const ids = await asyncPool(files, PROMPT_HISTORY_CONCURRENCY, recordPromptHistory);
			for (const [index, entry] of files.entries()) {
				updateFile(entry.id, { promptHistoryId: ids[index] });
			}
			return new Map(files.map((entry, index) => [entry.id, ids[index]]));
		} finally {
			setIsRecording(false);
		}
	};

	const runTransforms = async () => {
		if (!departmentId || files.length === 0) return;
		const toTransform = files.filter((entry) => entry.mode === "transform");
		track("data_load_run_start", {
			department_id: departmentId,
			file_count: files.length,
			transform_count: toTransform.length,
			direct_count: files.length - toTransform.length,
		});

		const idByFile = await recordAll();
		if (toTransform.length > 0) {
			setIsTransforming(true);
			try {
				const outcomes = await asyncPool(toTransform, TRANSFORM_CONCURRENCY, (entry) => transformOne(entry, idByFile.get(entry.id) ?? null));
				reportOutcome(outcomes.filter((ok) => !ok).length, toTransform.length);
			} finally {
				setIsTransforming(false);
			}
		}
		onDone();
	};

	return { isRecording, isTransforming, runTransforms };
}
